<script lang="ts" setup>
import Popup from './Popup.vue';

interface StatusComponent {
  id: string;
  name: string;
  status: string;
  updatedAt: string;
  description?: string;
  groupId?: string;
}

interface Props {
  modelValue: boolean;
  components: StatusComponent[];
  isLoading: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

function getStatusIcon(status: string) {
  switch (status) {
    case "operational":
      return "mdi-check-circle";
    case "under_maintenance":
      return "mdi-wrench";
    case "degraded_performance":
    case "partial_outage":
      return "mdi-alert-circle";
    case "major_outage":
    case "down":
      return "mdi-close-circle";
    default:
      return "mdi-help-circle";
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case "operational":
      return "success";
    case "degraded_performance":
    case "partial_outage":
      return "warning";
    case "major_outage":
    case "down":
      return "error";
    case "under_maintenance":
      return "info";
    default:
      return "info";
  }
}

function getTranslation(status: string) {
  switch (status) {
    case "operational":
      return "Operationeel";
    case "under_maintenance":
      return "Onderhoud";
    case "degraded_performance":
      return "Verminderd";
    case "partial_outage":
      return "Gedeeltelijke storing";
    case "major_outage":
      return "Grote storing";
    case "down":
      return "Uitgevallen";
    default:
      return status;
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleString("nl-NL");
}

function closeDialog() {
  emit('update:modelValue', false);
}
</script>

<template>
<Popup :width="600" :modal-open="modelValue" :title="'Status Dashboard'" :sub-title="'Bekijk de status van de componenten'" :persistent="true" @onDialogResult="closeDialog">
  <template #body>
    <v-progress-linear v-if="isLoading" indeterminate></v-progress-linear>
        
        <v-list v-else>
          <v-list-item 
            v-for="component in components" 
            :key="component.id"
            class="component-item"
          >
            <template v-slot:prepend>
              <v-icon 
                :color="getStatusColor(component.status)"
                size="small"
              >
                {{ getStatusIcon(component.status) }}
              </v-icon>
            </template>
            
            <v-list-item-title>
              {{ component.name }}
            </v-list-item-title>
            
            <v-list-item-subtitle>
              <div class="component-status">
                <strong>{{ getTranslation(component.status) }}</strong>
                <span class="ml-2">{{ formatDate(component.updatedAt) }}</span>
              </div>
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
  </template>
  <template #footer>
    <v-card-actions class="justify-end">
        <v-btn 
          href="https://integraleduurzaamheidsoplossing.statuspage.io/" 
          target="_blank"
          variant="text"
          size="small"
        >
          Ga naar historische data
        </v-btn>
      </v-card-actions>

  </template>
</Popup>
</template>

<style scoped>
.component-item {
  border-bottom: 1px solid #eee;
}

.component-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.component-description {
  margin-top: 4px;
  font-size: 11px;
  color: #f44336;
  font-style: italic;
}
</style>
